// Sorter race — the Newport / Poland stand-in for "Race to the cut-off"
// (Alex, 2026-10-20; plan .claude/plans/feat-sorter-dashboard.md). One
// card per station (Sorter 1, Sorter 2): the batch the station is on
// (its date and batch number), how far through it is, and a projected
// finish graded against a TARGET finish instead of a cut-off:
//
//   target = today's first scan on the batch + items left at that point
//            ÷ (the 30-day PER-PERSON rate × the station's sorting bays)
//
// i.e. what a FULL team — one person per bay — should manage for the whole
// sort (Alex, 2026-10-20); the live pace is what the people actually on
// the station are doing. Fixed for the day, and a batch carried over from
// the previous shift is not judged on the overnight gap. Newport's Sorter 1
// is the conveyor (no people), so it keeps the station's own 30-day rate. The pace is the race's own weighted 15-minute slices
// (weightedPace); the bands are the race's, measured against the target
// (decision 4 — 30 minutes either side, same for every batch size):
//   Flying 🔥   — projected finish ≥ FLYING_MARGIN_MIN before the target
//   On track 😎 — before the target
//   Behind 😮   — up to DANGER_AFTER_MIN past the target
//   Danger ⚠️   — later than that
// No cut-off: the card never freezes, and there is no time lorry. Sorter
// 2 works several dates/batches at once, so the other live batches are
// listed under the headline one (decision 2).
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../i18n";
import { SorterBatchRow, SorterRace } from "../api/dashboardActivity";
import {
    BAND_HOLD_POLLS, DANGER_AFTER_MIN, FLYING_MARGIN_MIN, Pace, RaceBand, bandColour, sameLondonDay, weightedPace,
} from "./DashboardRace";

const fmtInt = (n: number) => n.toLocaleString();

export interface SorterLaneState {
    pct: number;
    remaining: number;
    etaMs: number | null;
    band: RaceBand;
}

// Pure: the runner's position, estimate and band against the target.
// Without a target (no 30-day history yet, or no scan today) the lane
// still projects a finish but carries no band.
export const sorterLaneState = (
    done: number, total: number, pace: Pace, nowMs: number, targetMs: number | null, finished = false,
): SorterLaneState => {
    const remaining = Math.max(0, total - done);
    const pct = total > 0 ? Math.min(100, Math.floor((done / total) * 100)) : 0;
    if (finished || (total > 0 && remaining === 0)) return { pct: 100, remaining: 0, etaMs: null, band: "done" };
    if (pace.perMs === null || pace.perMs <= 0 || total === 0) {
        return { pct, remaining, etaMs: null, band: "idle" };
    }
    const etaMs = nowMs + remaining / pace.perMs;
    let band: RaceBand = "idle";
    if (targetMs !== null) {
        if (etaMs > targetMs + DANGER_AFTER_MIN * 60_000) band = "danger";
        else if (etaMs > targetMs) band = "behind";
        else if (etaMs <= targetMs - FLYING_MARGIN_MIN * 60_000) band = "flying";
        else band = "on_track";
    }
    return { pct, remaining, etaMs, band };
};

const PACE_BANDS: ReadonlySet<RaceBand> = new Set(["flying", "on_track", "behind", "danger"]);

const STATUS: Partial<Record<RaceBand, { key: string; emoji: string }>> = {
    flying: { key: "dashboard_team_race_status_flying", emoji: "🔥" },
    on_track: { key: "dashboard_team_race_status_on_track", emoji: "😎" },
    behind: { key: "dashboard_team_race_status_behind", emoji: "😮" },
    danger: { key: "dashboard_team_race_status_danger", emoji: "⚠️" },
    done: { key: "dashboard_team_race_finished", emoji: "🏆" },
    idle: { key: "dashboard_team_race_no_estimate", emoji: "⏳" },
};

const runnerEmoji = (band: RaceBand) => (band === "done" ? "🏆" : band === "idle" ? "🧍" : "🏃");

const londonClock = (ms: number) =>
    new Date(ms).toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" });

// "Mon 19 Oct" from the batch's YYYY-MM-DD.
export const batchDateLabel = (iso: string | null) =>
    iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) : "—";

interface Props {
    sorter: 1 | 2;
    /** null = no batch scanned today; undefined = payload from an older BE / snapshot. */
    race: SorterRace | null | undefined;
    nowMs?: number;
}

const DashboardSorterRace = ({ sorter, race, nowMs = Date.now() }: Props) => {
    const { t } = useTranslation();
    const title = t(sorter === 1 ? "sorter_1" : "sorter_2");

    const targetMs = race?.target_at ? Date.parse(race.target_at) : null;
    const startMs = race?.window_started_at ? Date.parse(race.window_started_at) : null;
    const pace = weightedPace(
        (race?.buckets ?? []).map((b) => ({ age: b.age, items: b.items, orders: 0 })),
        race?.window_minutes ?? 15, startMs, nowMs,
    );
    const s = race
        ? sorterLaneState(race.items_done, race.items_total, pace, nowMs, targetMs, race.finished)
        : { pct: 0, remaining: 0, etaMs: null, band: "idle" as RaceBand };

    // Same hysteresis as the race: a pace band shows once it has held for
    // BAND_HOLD_POLLS consecutive payloads.
    const [shownBand, setShownBand] = useState<RaceBand>(s.band);
    const pending = useRef<{ band: RaceBand; count: number } | null>(null);
    const lastPayload = useRef<string | null>(null);
    const generatedAt = race?.generated_at ?? "";
    useEffect(() => {
        if (generatedAt === lastPayload.current) return;
        lastPayload.current = generatedAt;
        if (!PACE_BANDS.has(s.band) || !PACE_BANDS.has(shownBand)) {
            pending.current = null;
            setShownBand(s.band);
            return;
        }
        if (s.band === shownBand) { pending.current = null; return; }
        const p = pending.current;
        const count = p && p.band === s.band ? p.count + 1 : 1;
        if (count >= BAND_HOLD_POLLS) {
            pending.current = null;
            setShownBand(s.band);
        } else {
            pending.current = { band: s.band, count };
        }
    }, [generatedAt, s.band, shownBand]);

    const band = shownBand;
    const colour = bandColour(band);
    const running = s.etaMs !== null;
    const status = STATUS[band];

    const batchLabel = race
        ? t("dashboard_team_sorter_batch", { date: batchDateLabel(race.date), n: String(race.batch ?? "—") })
        : t("dashboard_team_sorter_no_batch");

    const otherRow = (b: SorterBatchRow) => {
        const pct = b.items_total > 0 ? Math.min(100, Math.floor((b.items_done / b.items_total) * 100)) : 0;
        return (
            <li key={`${b.date}|${b.batch}`} className="tdash-sorter-other">
                <span className="tdash-sorter-other-name">
                    {t("dashboard_team_sorter_batch", { date: batchDateLabel(b.date), n: String(b.batch ?? "—") })}
                </span>
                <span className="tdash-sorter-other-count">
                    {t("dashboard_team_sorter_items_count", { done: fmtInt(b.items_done), total: fmtInt(b.items_total) })}
                </span>
                <span className="tdash-sorter-other-pct">{pct}%</span>
                <span className="tdash-sorter-other-track" aria-hidden="true">
                    <span className="tdash-sorter-other-fill" style={{ width: `${pct}%` }} />
                </span>
            </li>
        );
    };

    return (
        <section className={`tdash-race-card tdash-race-card--${band} tdash-race-card--sorter`}>
            <div className="tdash-race-main">
                <div className="tdash-race-head">
                    <span className="tdash-label">{title}</span>
                    <span className="tdash-caption tdash-sorter-batch">{batchLabel}</span>
                </div>
                {status && (
                    <div className="tdash-race-status" style={{ color: colour }}>
                        <span className="tdash-race-status-text">{t(status.key)}</span>
                        <span className="tdash-race-status-emoji" role="img" aria-hidden="true">{status.emoji}</span>
                    </div>
                )}
                <div className="tdash-race-track-wrap">
                    <div className="tdash-race-track">
                        <div className="tdash-race-fill" style={{ width: `${s.pct}%`, backgroundColor: colour }} />
                        <span
                            className={`tdash-race-runner${running ? " tdash-race-runner--running" : ""}`}
                            style={{ left: `${s.pct}%` }}
                            role="img"
                            aria-label={`${s.pct}%`}
                        >
                            {runnerEmoji(band)}
                        </span>
                    </div>
                    <span className="tdash-race-finish" role="img" aria-label="finish">🏁</span>
                </div>
                <div className="tdash-race-meta">
                    <span className="tdash-race-count">
                        {t("dashboard_team_sorter_items_count", {
                            done: fmtInt(race?.items_done ?? 0), total: fmtInt(race?.items_total ?? 0),
                        })}
                    </span>
                    <span className="tdash-race-items">
                        {t("dashboard_team_sorter_people_now", { n: fmtInt(race?.people_now ?? 0) })}
                    </span>
                    <span className="tdash-race-pct" style={{ color: colour }}>{s.pct}%</span>
                </div>
                {race && race.others.length > 0 && (
                    <div className="tdash-sorter-others">
                        <span className="tdash-caption">{t("dashboard_team_sorter_other_batches")}</span>
                        <ul className="tdash-sorter-other-list">{race.others.map(otherRow)}</ul>
                    </div>
                )}
            </div>
            <div className="tdash-race-projected">
                <span className="tdash-label">
                    {s.band === "done" ? t("dashboard_team_race_finish_time") : t("dashboard_team_race_projected")}
                </span>
                <span
                    className={`tdash-race-projected-time${s.etaMs !== null && !sameLondonDay(s.etaMs, nowMs) ? " tdash-race-projected-time--text" : ""}`}
                    style={{ color: colour }}
                >
                    {s.band === "done"
                        ? (race?.ended_at ? londonClock(Date.parse(race.ended_at)) : "—")
                        : s.etaMs === null
                            ? "—"
                            : sameLondonDay(s.etaMs, nowMs)
                                ? londonClock(s.etaMs)
                                : t("dashboard_team_race_not_today")}
                </span>
                {s.etaMs === null && status && (
                    <span className="tdash-caption">{t(status.key)}</span>
                )}
                {/* The target the band is measured against, and the two
                    rates behind it: today's pace and the 30-day average. */}
                <span className="tdash-caption tdash-sorter-target">
                    {targetMs !== null
                        ? t("dashboard_team_sorter_target", { time: londonClock(targetMs) })
                        : t("dashboard_team_sorter_no_target")}
                </span>
                <span className="tdash-caption tdash-race-rate">
                    {t("dashboard_team_race_rate", { n: fmtInt(pace.itemsPerHour) })}
                </span>
                {race?.target_basis === "person" && race.avg_person_items_per_hour != null ? (
                    <span className="tdash-caption tdash-race-rate">
                        {t("dashboard_team_sorter_average_person", {
                            n: fmtInt(Math.round(race.avg_person_items_per_hour)), days: String(race.avg_window_days),
                            bays: fmtInt(race.bays),
                        })}
                    </span>
                ) : race?.avg_items_per_hour != null && (
                    <span className="tdash-caption tdash-race-rate">
                        {t("dashboard_team_sorter_average", {
                            n: fmtInt(Math.round(race.avg_items_per_hour)), days: String(race.avg_window_days),
                        })}
                    </span>
                )}
            </div>
        </section>
    );
};

export default DashboardSorterRace;
